/**
 * Display formatting helpers for the landing-page YouTube feed.
 * Kept pure and framework-free so they can be unit-tested in isolation.
 */

/**
 * Formats a raw view count the way YouTube does, e.g.
 * 164 → "164 views", 1_234 → "1.2K views", 1_500_000 → "1.5M views".
 * Returns null when the count is unavailable/invalid.
 */
export function formatViewCount(count?: number | null): string | null {
    if (count == null || !Number.isFinite(count) || count < 0) {
        return null;
    }
    const compact = new Intl.NumberFormat("en", {
        notation: "compact",
        maximumFractionDigits: 1,
    }).format(count);
    return `${compact} ${count === 1 ? "view" : "views"}`;
}

/**
 * Converts an ISO 8601 duration (e.g. "PT58M31S", "PT1H0M28S") into a
 * clock-style label ("58:31", "1:00:28"). Returns null when unparseable.
 */
export function formatDuration(iso?: string | null): string | null {
    if (!iso) {
        return null;
    }
    const match = /^PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?$/.exec(iso);
    if (!match) {
        return null;
    }
    const hours = parseInt(match[1] ?? "0", 10);
    const minutes = parseInt(match[2] ?? "0", 10);
    const seconds = parseInt(match[3] ?? "0", 10);
    if (hours === 0 && minutes === 0 && seconds === 0) {
        return null;
    }
    const pad = (n: number) => String(n).padStart(2, "0");
    return hours > 0 ? `${hours}:${pad(minutes)}:${pad(seconds)}` : `${minutes}:${pad(seconds)}`;
}

/**
 * Produces a coarse "time ago" label ("3 months ago", "2 weeks ago",
 * "1 year ago"). Returns null when the date is missing/invalid.
 */
export function formatRelativeTime(iso?: string | null, now: Date = new Date()): string | null {
    if (!iso) {
        return null;
    }
    const then = new Date(iso);
    if (Number.isNaN(then.getTime())) {
        return null;
    }
    const seconds = Math.floor((now.getTime() - then.getTime()) / 1000);
    if (seconds < 60) {
        return "just now";
    }
    const units: Array<[string, number]> = [
        ["year", 31_536_000],
        ["month", 2_592_000],
        ["week", 604_800],
        ["day", 86_400],
        ["hour", 3_600],
        ["minute", 60],
    ];
    for (const [unit, unitSeconds] of units) {
        const value = Math.floor(seconds / unitSeconds);
        if (value >= 1) {
            return `${value} ${unit}${value === 1 ? "" : "s"} ago`;
        }
    }
    return "just now";
}

/**
 * Builds the "164 views • 3 months ago" metadata line, gracefully dropping
 * whichever part is unavailable. Returns null when neither part is present.
 */
export function formatVideoMeta(
    viewCount?: number | null,
    publishedAt?: string | null,
    now: Date = new Date()
): string | null {
    const parts = [formatViewCount(viewCount), formatRelativeTime(publishedAt, now)].filter(
        (part): part is string => Boolean(part)
    );
    return parts.length > 0 ? parts.join(" • ") : null;
}
